import { isReconciliationCredit } from './settlementCredits';

const money = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed)
    ? Math.round((parsed + Number.EPSILON) * 100) / 100
    : 0;
};

export const QUICK_RECONCILIATION_FIELDS = Object.freeze([
  'number',
  'date',
  'amount',
  'reason',
]);

export const validateQuickReconciliationInput = (values, today) => {
  if (!String(values?.number || '').trim()) return 'Número de NC requerido.';
  if (!values?.date) return 'Fecha requerida.';
  if (today && String(values.date).slice(0, 10) > String(today).slice(0, 10)) {
    return 'La fecha no puede ser futura.';
  }
  if (money(values?.amount) <= 0) return 'El monto debe ser mayor a cero.';
  return '';
};

export const pendingAdjustmentAmount = (invoice) => Math.max(0, money(
  invoice?.pendingAdjustmentAmount
  ?? invoice?.pending_adjustment_amount
  ?? invoice?.pendingCreditNoteAmount
  ?? invoice?.pending_credit_note_amount
  ?? 0
));

export const isQuickReconciliationCredit = (note) => Boolean(
  isReconciliationCredit(note)
  && String(note?.applicationMode || note?.application_mode || '').toLowerCase()
    === 'reconciliation'
  && !String(note?.linkedSuggestionKey || '').trim()
  && !String(note?.linkedType || '').trim()
);

export const quickNotesForInvoice = (notes, invoiceId) => (
  (notes || [])
    .map((note, index) => ({ note, index }))
    .filter(({ note }) => (
      Number(note?.invoiceId) === Number(invoiceId)
      && isQuickReconciliationCredit(note)
    ))
);

export const rebalanceQuickReconciliationNotes = (notes, invoices) => {
  const remainingByInvoice = Object.fromEntries(
    (invoices || []).map((invoice) => [
      Number(invoice.id),
      pendingAdjustmentAmount(invoice),
    ])
  );

  return (notes || []).map((note) => {
    if (!isQuickReconciliationCredit(note)) return note;
    const invoiceId = Number(note?.invoiceId);
    if (!invoiceId || remainingByInvoice[invoiceId] == null) return note;
    const noteAmount = Math.max(0, money(note?.amount));
    const allocationAmount = Math.min(
      noteAmount,
      Math.max(0, money(remainingByInvoice[invoiceId]))
    );
    remainingByInvoice[invoiceId] = money(
      remainingByInvoice[invoiceId] - allocationAmount
    );
    return {
      ...note,
      applicationMode: 'reconciliation',
      forAdjustmentsOnly: true,
      allocationAmount,
      remainingCreditAmount: money(noteAmount - allocationAmount),
      pendingAdjustmentAmount: pendingAdjustmentAmount(
        (invoices || []).find((invoice) => Number(invoice.id) === invoiceId)
      ),
    };
  });
};

export const reconciliationStateForInvoice = (invoice, notes) => {
  const pending = pendingAdjustmentAmount(invoice);
  const invoiceNotes = quickNotesForInvoice(notes, invoice?.id);
  const noteAmount = money(invoiceNotes.reduce(
    (total, { note }) => total + Math.max(0, money(note?.amount)),
    0
  ));
  const reconciled = money(invoiceNotes.reduce(
    (total, { note }) => total + Math.max(0, money(note?.allocationAmount)),
    0
  ));
  return {
    pending,
    noteAmount,
    reconciled: Math.min(pending, reconciled),
    remaining: Math.max(0, money(pending - reconciled)),
    excess: Math.max(0, money(noteAmount - reconciled)),
    count: invoiceNotes.length,
    notes: invoiceNotes,
    complete: pending > 0 && money(pending - reconciled) === 0,
  };
};

export const createQuickReconciliationNote = ({
  invoice,
  number,
  date,
  amount,
  reason = '',
}) => {
  const noteAmount = Math.max(0, money(amount));
  const pending = pendingAdjustmentAmount(invoice);
  const allocationAmount = Math.min(noteAmount, pending);
  return {
    number: String(number || '').trim(),
    date,
    amount: noteAmount,
    invoiceId: Number(invoice?.id),
    allocationAmount,
    remainingCreditAmount: money(noteAmount - allocationAmount),
    applicationMode: 'reconciliation',
    forAdjustmentsOnly: true,
    linkedSuggestionKey: '',
    linkedType: '',
    linkedSource: 'manual',
    linkedAdjustmentAmount: pending,
    pendingAdjustmentAmount: pending,
    reason: String(reason || '').trim(),
  };
};

export const buildBulkReconciliationDrafts = (invoices, notes, today) => (
  (invoices || []).flatMap((invoice) => {
    const state = reconciliationStateForInvoice(invoice, notes);
    if (state.remaining <= 0) return [];
    return [{
      invoice,
      invoiceId: Number(invoice.id),
      number: '',
      date: today,
      amount: state.remaining,
    }];
  })
);
